/**
 * waap-domain-tab-registry.ts — Static tab registry for the WAAP service detail page.
 *
 * Lists all 13 WAAP tabs (slugs, labels, sections) from the Wave B spec.
 * Mirrors cdn-domain-tab-registry.ts in structure. Kept separate so the
 * detail page stays under the 200-LOC guideline and B2–B4 implementors
 * can verify the full list at a glance.
 *
 * B1 (implemented): overview, websites, logs
 * B2 (implemented): analytics
 * B3 (implemented): aiwaf-general, aiwaf-settings, emergency, prog-mitigation
 * B4 (stub → coming soon): aiwaf-advanced, aiwaf-compliance, aiwaf-custom-rules,
 *                           aiwaf-wac, bot-management, api-protection
 */

/* ── All 13 tab slugs ────────────────────────────────────────────────────── */

/**
 * Canonical tab value list — matches the ?tab= URL param.
 * Order controls visual order of triggers.
 */
export const WAAP_TABS = [
  // B1 — Foundation (this wave)
  'overview',
  'websites',
  'logs',
  // B2 — Analytics (7 sub-boards)
  'analytics',
  // B3 — AI-WAF account-level config
  'aiwaf-general',
  'aiwaf-settings',
  'emergency',
  'prog-mitigation',
  // B4 — Full CRUD tabs
  'aiwaf-advanced',
  'aiwaf-compliance',
  'aiwaf-custom-rules',
  'aiwaf-wac',
  'bot-management',
  'api-protection',
] as const;

export type WaapTab = (typeof WAAP_TABS)[number];

export const DEFAULT_WAAP_TAB: WaapTab = 'overview';

/** Tabs implemented in B1 — render their real components. */
export const WAAP_B1_TABS = new Set<WaapTab>(['overview', 'websites', 'logs']);

/** Tabs implemented in B2 — Analytics (7 sub-boards). */
export const WAAP_B2_TABS = new Set<WaapTab>(['analytics']);

/** Tabs implemented in B3 — account-level mutation tabs. */
export const WAAP_B3_TABS = new Set<WaapTab>([
  'aiwaf-general',
  'aiwaf-settings',
  'emergency',
  'prog-mitigation',
]);

/* ── Human-readable labels ───────────────────────────────────────────────── */

export const WAAP_TAB_LABEL: Record<WaapTab, string> = {
  overview:          'Overview',
  websites:          'Websites',
  logs:              'Logs',
  analytics:         'Analytics',
  'aiwaf-general':   'AI-WAF General',
  'aiwaf-settings':  'AI-WAF Settings',
  emergency:         'Emergency',
  'prog-mitigation': 'Programmable',
  'aiwaf-advanced':  'AI-WAF Advanced',
  'aiwaf-compliance': 'Compliance',
  'aiwaf-custom-rules': 'Custom Rules',
  'aiwaf-wac':       'Web Access Control',
  'bot-management':  'Bot Management',
  'api-protection':  'API Protection',
};

/* ── Section groupings (visual separators in the trigger list) ───────────── */

export interface WaapTabSection {
  /** Empty string = no section label rendered before these triggers. */
  label: string;
  tabs: WaapTab[];
}

export const WAAP_TAB_SECTIONS: WaapTabSection[] = [
  { label: '', tabs: ['overview', 'websites', 'logs'] },
  { label: 'Analytics', tabs: ['analytics'] },
  {
    label: 'AI-WAF',
    tabs: [
      'aiwaf-general',
      'aiwaf-settings',
      'aiwaf-advanced',
      'aiwaf-compliance',
      'aiwaf-custom-rules',
      'aiwaf-wac',
    ],
  },
  {
    label: 'Protection',
    tabs: [
      'emergency',
      'prog-mitigation',
      'bot-management',
      'api-protection',
    ],
  },
];

/** B2-B4 stub tabs — render "coming soon" EmptyState in B1. */
export const WAAP_STUB_TABS = WAAP_TABS.filter(
  (t): t is Exclude<WaapTab, 'overview' | 'websites' | 'logs'> =>
    !WAAP_B1_TABS.has(t as WaapTab),
);
